import { useMemo } from 'react';
import {
  isPasswordValid,
  isUserNameContainedInPassword,
  doesPasswordContainForbiddenKeywords,
} from '@tradingblock/components';
import { UserRequirementsInfo } from '@tradingblock/types';

interface PasswordStrengthResult {
  strength: number;
  class: string;
  text: string;
}

export const usePasswordStrength = (
  password: string,
  styles: any,
  userRequirements?: UserRequirementsInfo,
  userName?: string,
  firstName?: string,
  lastName?: string
): PasswordStrengthResult => {
  const calculatePasswordStrength = (password: string): number => {
    if (!password) return 0;

    let strength = 0;

    // Length contribution (up to 25 points)
    if (password.length >= 8) strength += 10;
    if (password.length >= 12) strength += 10;
    if (password.length >= 16) strength += 5;

    // Character variety contribution (up to 50 points)
    if (/[a-z]/.test(password)) strength += 10;
    if (/[A-Z]/.test(password)) strength += 10;
    if (/[0-9]/.test(password)) strength += 10;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 10;
    if (/[^a-zA-Z0-9!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 10; // Additional special chars

    // Complexity contribution (up to 25 points)
    const uniqueChars = new Set(password).size;
    if (uniqueChars >= 8) strength += 10;
    if (uniqueChars >= 12) strength += 10;
    if (uniqueChars >= 16) strength += 5;

    return Math.min(100, strength);
  };

  const calculateDynamicPasswordStrength = (password: string, requirements?: UserRequirementsInfo): number => {
    if (!password || !requirements) return calculatePasswordStrength(password);

    let strength = 0;
    const { passwordRequirements } = requirements;

    // Base length contribution (up to 30 points)
    const minLength = passwordRequirements.minLength || 8;
    const maxLength = passwordRequirements.maxLength || 128;

    if (password.length >= minLength) strength += 15;
    if (password.length >= minLength + 4) strength += 10;
    if (password.length >= minLength + 8) strength += 5;

    // Don't penalize for being within max length, but don't reward excessive length
    if (password.length > maxLength) {
      strength = Math.max(0, strength - 10);
    }

    // Character type requirements (up to 40 points)
    const minLower = passwordRequirements.minLowerCount || 1;
    const minUpper = passwordRequirements.minUpperCount || 1;
    const minDigits = passwordRequirements.minDigitCount || 1;
    const minSpecial = passwordRequirements.minSpecialCount || 1;

    const lowerCount = (password.match(/[a-z]/g) || []).length;
    const upperCount = (password.match(/[A-Z]/g) || []).length;
    const digitCount = (password.match(/[0-9]/g) || []).length;
    const specialCount = (password.match(/[!@#$%^&*(),.?":{}|<>]/g) || []).length;

    // Reward for meeting minimums
    if (lowerCount >= minLower) strength += 10;
    if (upperCount >= minUpper) strength += 10;
    if (digitCount >= minDigits) strength += 10;
    if (specialCount >= minSpecial) strength += 10;

    // Bonus for exceeding minimums (up to 20 points)
    if (lowerCount > minLower) strength += 5;
    if (upperCount > minUpper) strength += 5;
    if (digitCount > minDigits) strength += 5;
    if (specialCount > minSpecial) strength += 5;

    // Complexity contribution (up to 10 points)
    const uniqueChars = new Set(password).size;
    if (uniqueChars >= 8) strength += 5;
    if (uniqueChars >= 12) strength += 5;

    return Math.min(100, strength);
  };

  const getPasswordStrengthClass = (strength: number): string => {
    if (strength < 40) return styles.strengthWeak;
    if (strength < 80) return styles.strengthMedium;
    return styles.strengthStrong;
  };

  const getPasswordStrengthText = (strength: number): string => {
    if (strength < 40) return 'Weak';
    if (strength < 80) return 'Medium';
    return 'Strong';
  };

  return useMemo(() => {
    // Use dynamic calculation if requirements are available
    let strength = userRequirements
      ? calculateDynamicPasswordStrength(password, userRequirements)
      : calculatePasswordStrength(password);

    // Apply penalties for security issues
    if (userName && isUserNameContainedInPassword(password, userName)) {
      strength = Math.max(0, strength - 30);
    }

    if (doesPasswordContainForbiddenKeywords(password)) {
      strength = Math.max(0, strength - 40);
    }

    if (firstName && password && password.toLowerCase().includes(firstName.toLowerCase())) {
      strength = Math.max(0, strength - 20);
    }

    if (lastName && password && password.toLowerCase().includes(lastName.toLowerCase())) {
      strength = Math.max(0, strength - 20);
    }

    // Additional validation against requirements
    if (userRequirements && password) {
      const isValid = isPasswordValid(password, userRequirements, userName);
      if (!isValid) {
        strength = Math.max(0, strength - 25);
      }
    }

    return {
      strength,
      class: getPasswordStrengthClass(strength),
      text: getPasswordStrengthText(strength),
    };
  }, [password, styles, userRequirements, userName, firstName, lastName]);
};

export default usePasswordStrength;
